/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
    makeStyles,
    useMediaQuery,
} from '@material-ui/core';
import { useUIDSeed } from 'react-uid';
import {
    arrayOf, func, object, shape, string,
} from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import mbpLogger from '@module-logger/mbp';

import { ArrowForwardIosRounded, ArrowBackIosRounded, Height } from '@material-ui/icons';
import { useQuery } from '@apollo/client';
import { setCategoryTabs } from '../../../../../state/ducks/App/App-Actions';
import noop from '../../../../helpers/noop';
import { trackEvent } from '../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { emitCustomTrackingEvent } from '../../../../../state/ducks/TagManager/ducks/ClickStreamEvents/ClickStreamEvent-Actions';
import { getSalesforceAttributes } from '../../../../../state/ducks/App/App-Selectors';
import { getBrandName } from '../../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import findContentByUID from '../../../../gql/queries/findContentByUID';
import {
    BANNER_IMPRESSION_EVENT,
    BANNER_INTERACTION_EVENT,
    buildTrendingTabEvent,
} from './trendingTabsTracking';
import { setActiveBannerProps } from '../../../../helpers/tracking/banner/activeBannerHelpers';

const useStyles = makeStyles((theme) => ({
    root: (styles) => ({
        maxWidth: styles.maxWidth || '',
        margin: styles?.margin || '5px auto 20px',
        marginLeft: 'auto',
        marginRight: 'auto',
        padding: '0 10px',
        [theme.breakpoints.up(1024)]: {
            padding: '0 20px',
        },
        [theme.breakpoints.up(1400)]: {
            padding: '0',
        },
    }),
    wrapper: {
        width: '100%',
        cursor: 'pointer',
        display: 'flex',
    },
    tabsScroll: {
        display: 'flex',
        overflowX: 'auto',
        scrollBehavior: 'smooth',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': {
            display: 'none',
        },
    },
    tab: (styles) => ({
        padding: '10px 20px',
        fontSize: styles?.font_size || '16px',
        fontFamily: styles?.font_family || 'inherit',
        cursor: 'pointer',
        borderBottom: `3px solid ${styles?.border_color?.color}`,
        whiteSpace: 'nowrap',
        fontWeight: '500',
        color: styles?.color?.color || '#767571',
        '&:hover': {
            borderBottom: `3px solid ${styles?.hover_color?.color}`,
            color: styles?.hover_color?.color || '#483f6b',
        },
    }),
    active: (styles) => ({
        borderBottom: `3px solid ${styles?.hover_color?.color}`,
        color: styles?.hover_color?.color || '#483f6b',
        fontWeight: '700',
    }),
    controls: (styles) => ({
        display: 'flex',
        gap: '10px',
        borderBottom: `3px solid ${styles?.border_color?.color}`,
        paddingLeft: '20px',
    }),
    button: (styles) => ({
        border: '0',
        cursor: 'pointer',
        background: 'none',
        '& svg': {
            fontSize: styles?.icon_font_size || '16px',
        },
    }),
    disabledControl: {
        opacity: '0.9',
        cursor: 'not-allowed',
        pointerEvents: 'none',
    },
    tabRoot: {
        maxWidth: '1400px',
        margin: '12px auto',
        [theme.breakpoints.down(1400)]: {
            margin: '12px 20px 0px 20px',
        },
        [theme.breakpoints.down(1024)]: {
            margin: '10px 10px 0px 10px',
        },
    },
    tabContainer: {
        display: 'flex',
        width: '70%',
        height: '45px',
        margin: '0px 0px 16px 0px',
        borderBottom: '3px solid #E7EDF1',
        [theme.breakpoints.down(600)]: {
            width: '100%',
        },
    },
    tabBox: {
        display: 'flex',
        width: '15%',
        padding: '10px 20px',
        [theme.breakpoints.down(600)]: {
            minWidth: '30%',
            overflowX: 'scroll',
        },
    },
    tabText: {
        // margin: '0px 16px',
        width: '100%',
        height: '20px',
        borderRadius: '6px',
        background: 'linear-gradient(270deg, #E7EDF1, #F2F8FC, #E7EDF1)',
        backgroundSize: '1000% 1000%',
    },
}));

export const CategoryTabs = ({ maxWidth, categoryTabs, trackClickThrough }) => {
    const categoryStyles = categoryTabs?.styles || {};
    const classes = useStyles({
        maxWidth,
        ...categoryStyles,
    });
    const defaultSelectedIdx = (categoryTabs?.default_selected_index || 0) - 1;

    const dispatch = useDispatch();
    const seed = useUIDSeed();
    const scrollRef = useRef(null);
    const wrapperRef = useRef(null);
    const [categoryIndex, setCategoryIndex] = useState(defaultSelectedIdx);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const isDesktop = useMediaQuery('(min-width: 600px)');
    const salesforceAttributes = useSelector(getSalesforceAttributes);

    useEffect(() => {
        categoryTabs?.categories?.forEach((category) => {
            dispatch(trackEvent(buildTrendingTabEvent({
                eventName: BANNER_IMPRESSION_EVENT,
                bannerGroup: 'Trending Tabs Impression',
                category,
            })));
        });
    }, [categoryTabs, dispatch]);

    useEffect(() => {
        if (salesforceAttributes?.categoryTabs?.referenceProductCategory) {
            const idx = categoryTabs?.categories?.findIndex(
                (ele) => ele?.key === salesforceAttributes.categoryTabs.referenceProductCategory
            );
            setCategoryIndex(idx);
        }
    }, [salesforceAttributes?.categoryTabs]);

    useEffect(() => {
        const updateScrollButtons = () => {
            const el = scrollRef.current;
            if (!el) return;
            setCanScrollPrev(el.scrollLeft > 0);
            setCanScrollNext(el.scrollLeft + el.clientWidth < el.scrollWidth);
        };

        updateScrollButtons();
        scrollRef.current?.addEventListener('scroll', updateScrollButtons);
        if (typeof window !== 'undefined') {
            window.addEventListener('resize', updateScrollButtons);
        }

        return () => {
            scrollRef.current?.removeEventListener('scroll', updateScrollButtons);
            if (typeof window !== 'undefined') {
                window.removeEventListener('resize', updateScrollButtons);
            }
        };
    }, [categoryTabs]);

    const handleClick = (index, tabTitle) => {
        const category = categoryTabs?.categories[index];
        const bannerGroup = categoryTabs?.tracking?.feature_element || 'Trending Gifts';
        const bannerItemName = category?.title || tabTitle || '';

        setActiveBannerProps('Trending Tab', bannerGroup, bannerItemName);

        dispatch(trackEvent(buildTrendingTabEvent({
            eventName: BANNER_INTERACTION_EVENT,
            bannerGroup: categoryTabs?.tracking?.feature_element || 'Trending Tabs Click',
            category,
        })));

        if (categoryIndex === index) {
            setCategoryIndex(defaultSelectedIdx);
            dispatch(
                setCategoryTabs({
                    referenceProductID: '',
                    referenceProductCategory: '',
                    categoryId: '',
                }),
            );
            return;
        }

        trackClickThrough(category?.category_id ? category?.category_id : category?.key, tabTitle, categoryTabs?.tracking);

        dispatch(
            setCategoryTabs({
                referenceProductID: category?.product_id,
                referenceProductCategory: category?.key,
                categoryId: category?.category_id,
            }),
        );
        setCategoryIndex(index);
    };

    const handleKeyDown = (event, index, tabTitle) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleClick(index, tabTitle);
        }
    };

    const handlePrev = () => {
        if (scrollRef.current) {
            scrollRef.current.scrollBy({ left: -200, behavior: 'smooth' });
        }
    };

    const handleNext = () => {
        if (scrollRef.current) {
            scrollRef.current.scrollBy({ left: 200, behavior: 'smooth' });
        }
    };

    if (categoryTabs?.categories?.length > 0) {
        const showControls = isDesktop && (canScrollNext || canScrollPrev);

        return (
            <div className={classes.root}>
                <div className={classes.wrapper} ref={wrapperRef}>
                    <div className={classes.tabsScroll} ref={scrollRef}>
                        {categoryTabs.categories.map((item, index) => (
                            <div
                                key={seed(item)}
                                className={`${classes.tab} ${categoryIndex === index ? classes.active : ''}`}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(event) => handleKeyDown(event, index, item?.title)}
                                onClick={() => handleClick(index, item?.title)}
                            >
                                {item?.title}
                            </div>
                        ))}
                    </div>

                    {
                        showControls && (
                            <div className={classes.controls}>
                                <button
                                    className={`${classes.button} ${!canScrollPrev ? classes.disabledControl : ''}`}
                                    type="button"
                                    onClick={handlePrev}
                                    disabled={!canScrollPrev}
                                >
                                    <ArrowBackIosRounded />
                                </button>
                                <button
                                    className={`${classes.button} ${!canScrollNext ? classes.disabledControl : ''}`}
                                    type="button"
                                    onClick={handleNext}
                                    disabled={!canScrollNext}
                                >
                                    <ArrowForwardIosRounded />
                                </button>
                            </div>
                        )
                    }
                </div>
            </div>
        );
    }

    return null;
};

CategoryTabs.propTypes = {
    maxWidth: string,
    categoryTabs: shape({
        categories: arrayOf(
            shape({
                label: string,
            })),
    }),
    trackClickThrough: func,
};

CategoryTabs.defaultProps = {
    maxWidth: '',
    categoryTabs: { categories: [] },
    trackClickThrough: noop,
};

const NavigationPillsContainer = ({
    navPillsUID, maxWidth,
}) => {
    if (!navPillsUID) return null;
    const dispatch = useDispatch();
    const classes = useStyles();

    const brandName = useSelector(getBrandName);

    const { loading, error, data } = useQuery(
        findContentByUID({
            queryName: 'NavigationPills',
            brand: brandName,
            uid: navPillsUID?.uid,
            contentType: navPillsUID?._content_type_uid,
        }),
        {
            skip: !navPillsUID?.uid,
            fetchPolicy: 'cache-first',
        },
    );

    if (loading) {
        return (
            <div className={classes.tabRoot}>
                <div className={classes.tabContainer}>
                    <div className={classes.tabBox}>
                        <div className={`${classes.tabText} ${classes.shimmer}`} />
                    </div>
                    <div className={classes.tabBox}>
                        <div className={`${classes.tabText} ${classes.shimmer}`} />
                    </div>
                    <div className={classes.tabBox}>
                        <div className={`${classes.tabText} ${classes.shimmer}`} />
                    </div>
                    <div className={classes.tabBox}>
                        <div className={`${classes.tabText} ${classes.shimmer}`} />
                    </div>
                    <div className={classes.tabBox}>
                        <div className={`${classes.tabText} ${classes.shimmer}`} />
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        mbpLogger.logError({
            function: `NavigationPillsContainer - navPillsUID = ${navPillsUID}`,
            message: 'Error loading API data',
            appName: process.env.npm_package_name,
            module: 'NavigationPillsContainer',
            jsError: error,
        });
        return null;
    }

    const handleClick = (category, tabTitle, tracking) => {
        dispatch(emitCustomTrackingEvent({
            action: 'Click',
            category,
            page: { type: 'home' },
            eventSubType: 'feature-tracking',
        }));
        dispatch(trackEvent({
            eventCategory: tracking?.feature_category || 'Homepage',
            eventAction: tracking?.feature_element || 'Trending Tabs Click',
            eventLabel: tabTitle?.split(' ')?.join('-')?.toLowerCase(), // tab name
        }));
    };

    if (data?.findContentByUID?.content?.entry) {
        return (
            <CategoryTabs maxWidth={maxWidth} categoryTabs={data?.findContentByUID?.content?.entry} trackClickThrough={handleClick} />
        );
    }
    return null;
};

NavigationPillsContainer.propTypes = {
    maxWidth: string,
    navPillsUID: object,
};

NavigationPillsContainer.defaultProps = {
    maxWidth: '',
    navPillsUID: null,
};

export default NavigationPillsContainer;
